import * as React from 'react';
import {
  PartnerId,
  productBySlugQuery_productBySlug_partners,
  applyPartnerDiscountMutationVariables,
  applyPartnerDiscountMutation,
  PlaceSubscriptionOrder
} from '../../core-types';
import { Formik, Form, Field, FieldProps, FormikErrors } from 'formik';
import {
  Box,
  LoadableButton,
  Dialog,
  Button,
  ButtonGroup,
  Alert,
  Text
} from '../../components';
import { useMutation } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { globalMessages } from '../../lib/messages';
import { APPLY_PARTNER_DISCOUNT_MUTATION } from './graphql';
import { CssGrid } from '@urbaninfrastructure/react-ui-kit';
import { InputField } from '../BuyProduct/Step/InputField';
import { messages } from './messages';
import { AtmCardPreview } from './AtmCardPreview';

interface ATMValues {
  identificationNumber: string;
  serialNumber: string;
  fiscalCode: string;
}

const identificationNumberMinLength = 1;
const identificationNumberMaxLength = 4;
const serialNumberLength = 9;
const fiscalCodeLength = 16;

export function PartnerAtmDialogForm({
  heading,
  renderOpenButton,
  loading,
  error,
  onSubmit
}: {
  heading: React.ReactNode;
  renderOpenButton: (
    openPortal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  ) => React.ReactNode;
  loading: boolean;
  error: React.ReactNode;
  onSubmit: (values: ATMValues, closePortal: () => void) => void;
}) {
  const intl = useIntl();
  return (
    <Dialog
      data-testid="Dialog__ATM"
      closeOnOutsideClick={false}
      closeOnEsc={false}
      showCloseButton={false}
      heading={heading}
      renderOpenButton={renderOpenButton}
    >
      {({ closePortal }) => {
        return (
          <>
            {error}
            <Formik<ATMValues>
              initialValues={{
                identificationNumber: '',
                serialNumber: '',
                fiscalCode: ''
              }}
              validate={values => {
                const errors: FormikErrors<ATMValues> = {};

                if (
                  values.identificationNumber.length <
                    identificationNumberMinLength ||
                  values.identificationNumber.length >
                    identificationNumberMaxLength
                ) {
                  errors.identificationNumber = intl.formatMessage(
                    messages.ERR_ATM_ID_NUMBER_FORMAT
                  );
                }

                if (!/^\d+$/.test(values.identificationNumber)) {
                  errors.identificationNumber = intl.formatMessage(
                    messages.ERR_ATM_ID_NUMBER_FORMAT
                  );
                }

                if (values.fiscalCode.length !== fiscalCodeLength) {
                  errors.fiscalCode = intl.formatMessage(
                    messages.ERR_ATM_FISCAL_NUMBER_INVALID_LENGTH
                  );
                }

                if (values.serialNumber.length !== serialNumberLength) {
                  errors.serialNumber = intl.formatMessage(
                    messages.ERR_ATM_SERIAL_NUMBER_INVALID_LENGTH
                  );
                }

                if (!/^\d+$/.test(values.serialNumber)) {
                  errors.serialNumber = intl.formatMessage(
                    messages.ERR_ATM_SERIAL_NUMBER_INVALID_LENGTH
                  );
                }

                return errors;
              }}
              onSubmit={values => {
                onSubmit(values, closePortal);
              }}
            >
              {({ dirty }) => {
                return (
                  <Form>
                    <CssGrid gridGap="1.5rem" gridTemplateColumns="1fr 1fr">
                      <Box minWidth={{ md: 400 }}>
                        <Field name="identificationNumber">
                          {({
                            field,
                            form
                          }: FieldProps<
                            ATMValues['identificationNumber'],
                            ATMValues
                          >) => {
                            return (
                              <InputField
                                id={field.name}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                required
                                minLength={identificationNumberMinLength}
                                maxLength={identificationNumberMaxLength}
                                width={`${identificationNumberMaxLength +
                                  0.5}em`}
                                autoFocus
                                label={
                                  <FormattedMessage
                                    {...messages.identificationNumber}
                                  />
                                }
                                errorText={
                                  form.submitCount > 0 &&
                                  form.touched.identificationNumber
                                    ? form.errors.identificationNumber
                                    : undefined
                                }
                                {...field}
                              />
                            );
                          }}
                        </Field>
                        <Field name="serialNumber">
                          {({
                            field,
                            form
                          }: FieldProps<
                            ATMValues['serialNumber'],
                            ATMValues
                          >) => {
                            return (
                              <InputField
                                id={field.name}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                required
                                maxLength={serialNumberLength}
                                width={`${serialNumberLength}em`}
                                label={
                                  <FormattedMessage
                                    {...messages.serialNumber}
                                  />
                                }
                                errorText={
                                  form.submitCount > 0 &&
                                  form.touched.serialNumber &&
                                  form.errors.serialNumber
                                    ? form.errors.serialNumber
                                    : undefined
                                }
                                {...field}
                              />
                            );
                          }}
                        </Field>
                        <Field name="fiscalCode">
                          {({
                            field,
                            form
                          }: FieldProps<
                            ATMValues['fiscalCode'],
                            ATMValues
                          >) => {
                            return (
                              <InputField
                                id={field.name}
                                label={
                                  <FormattedMessage {...messages.fiscalCode} />
                                }
                                type="text"
                                maxLength={fiscalCodeLength}
                                required
                                width={`${fiscalCodeLength}em`}
                                errorText={
                                  form.submitCount > 0 &&
                                  form.touched.fiscalCode &&
                                  form.errors.fiscalCode
                                    ? form.errors.fiscalCode
                                    : undefined
                                }
                                {...field}
                              />
                            );
                          }}
                        </Field>
                        <ButtonGroup>
                          <LoadableButton
                            shape="cool"
                            size="small"
                            type="submit"
                            variant="primary"
                            loading={loading}
                            responseState={error ? 'error' : 'success'}
                          >
                            <FormattedMessage {...messages.atmApplyDiscount} />
                          </LoadableButton>
                          <Button
                            variant="link"
                            onClick={() => {
                              if (dirty) {
                                if (
                                  confirm(
                                    intl.formatMessage(
                                      globalMessages.confirmCloseDialog
                                    )
                                  )
                                ) {
                                  closePortal();
                                }
                              } else {
                                closePortal();
                              }
                            }}
                          >
                            Cancel
                          </Button>
                        </ButtonGroup>
                      </Box>
                      <Box maxWidth={300} mb="xs">
                        <Box mb="xs">
                          <AtmCardPreview />
                        </Box>
                        <Text>
                          A:{' '}
                          <FormattedMessage
                            {...messages.identificationNumber}
                          />
                        </Text>
                        <Text>
                          B: <FormattedMessage {...messages.serialNumber} />
                        </Text>
                      </Box>
                    </CssGrid>
                  </Form>
                );
              }}
            </Formik>
          </>
        );
      }}
    </Dialog>
  );
}

export function PartnersForm({
  order,
  partners,
  onDiscountVerified
}: {
  order: PlaceSubscriptionOrder;
  partners: productBySlugQuery_productBySlug_partners[];
  onDiscountVerified: (verifiedOrder: PlaceSubscriptionOrder) => void;
}) {
  const [applyPartnerDiscount, { loading, error, data }] = useMutation<
    applyPartnerDiscountMutation,
    applyPartnerDiscountMutationVariables
  >(APPLY_PARTNER_DISCOUNT_MUTATION, {
    onCompleted: data => {
      if (!data || data.applyPartnerDiscount.__typename !== 'Order') {
        return;
      }
      const { applyPartnerDiscount } = data;
      onDiscountVerified(applyPartnerDiscount);
    }
  });
  if (partners.length === 0) {
    return null;
  }
  const applyPartnerDiscountError =
    data &&
    data.applyPartnerDiscount.__typename === 'PartnerDiscountError' &&
    data.applyPartnerDiscount;
  return (
    <>
      {partners.map(partner => {
        switch (partner.id) {
          case PartnerId.milan_atm: {
            const applyAtmDiscountMessage = (
              <FormattedMessage {...messages.atmApplyDiscount} />
            );
            return (
              <React.Fragment key={partner.id}>
                <PartnerAtmDialogForm
                  heading={applyAtmDiscountMessage}
                  renderOpenButton={openPortal => {
                    return (
                      <Button type="button" onClick={openPortal} size="small">
                        {applyAtmDiscountMessage}
                      </Button>
                    );
                  }}
                  loading={loading}
                  error={
                    error || applyPartnerDiscountError ? (
                      <>
                        {error && (
                          <Alert variant="error">
                            <FormattedMessage
                              {...globalMessages.genericError}
                            />
                          </Alert>
                        )}
                        {applyPartnerDiscountError && (
                          <Alert variant="error">
                            {applyPartnerDiscountError.message || (
                              <FormattedMessage
                                {...globalMessages.genericError}
                              />
                            )}
                          </Alert>
                        )}
                      </>
                    ) : null
                  }
                  onSubmit={(values, closePortal) => {
                    applyPartnerDiscount({
                      variables: {
                        orderId: order.id,
                        partnerId: partner.id,
                        // details: `${values.identificationNumber};${values.serialNumber};${values.fiscalCode}`
                        milanATMInput: {
                          serialNumber: values.serialNumber,
                          fiscalCode: values.fiscalCode,
                          identificationNumber: parseInt(
                            values.identificationNumber
                          )
                        }
                      }
                    }).then(({ data }) => {
                      if (data) {
                        switch (data.applyPartnerDiscount.__typename) {
                          case 'Order': {
                            closePortal();
                            break;
                          }
                          case 'PartnerDiscountError': {
                            // Expected error handled in UI
                            break;
                          }
                        }
                      }
                    });
                  }}
                />
              </React.Fragment>
            );
          }
          default: {
            return null;
          }
        }
      })}
    </>
  );
}
